/** Shared zoom behaviour for the editor and the simulator. */
import type { Viewport } from '../../state/store';

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 120;
/** Screen pixels per mm at real size (CSS reference pixel: 96 per inch). */
export const REAL_SIZE_ZOOM = 96 / 25.4;
/** Factor per button click / keyboard step. */
export const ZOOM_STEP = 1.4;

export const clampZoom = (z: number): number => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

/** Zooms by `factor` keeping the screen point (mx, my) fixed. */
export function zoomAt(v: Viewport, factor: number, mx: number, my: number): Viewport {
  const zoom = clampZoom(v.zoom * factor);
  const f = zoom / v.zoom;
  return { zoom, panX: mx - (mx - v.panX) * f, panY: my - (my - v.panY) * f };
}

/**
 * Zoom factor for a wheel event. A mouse wheel notch (≈100 px) zooms ≈25 %. Trackpad pinch
 * arrives as ctrl+wheel with small deltas and gets a higher gain so it feels direct.
 */
export function wheelZoomFactor(e: { deltaY: number; deltaMode: number; ctrlKey: boolean }): number {
  const unit = e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 800 : 1;
  const dy = Math.max(-300, Math.min(300, e.deltaY * unit));
  return Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0022));
}

/** Zoom percentage relative to real size (100 % = actual stitch-out size on a 96 dpi screen). */
export const zoomPercent = (z: number): number => Math.round((z / REAL_SIZE_ZOOM) * 100);

/**
 * Viewport for a two-finger pinch: the canvas point under the fingers' starting midpoint follows
 * the current midpoint, and the zoom changes with the distance between the fingers.
 */
export function pinchViewport(start: Viewport, startMid: { x: number; y: number }, startDist: number, mid: { x: number; y: number }, dist: number): Viewport {
  const zoom = clampZoom(start.zoom * (startDist > 0 ? dist / startDist : 1));
  const wx = (startMid.x - start.panX) / start.zoom, wy = (startMid.y - start.panY) / start.zoom;
  return { zoom, panX: mid.x - wx * zoom, panY: mid.y - wy * zoom };
}
